import { forwardRef, useState } from "react";

import { createContext } from "@radix-ui/react-context";

import { ActionButton } from "../ActionButton";
import { CaptchaField, type CaptchaFieldProps } from "../CaptchaField";
import * as Dialog from "../Dialog";
import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

type RootProps = Dialog.RootProps;

const Root = Dialog.Root;

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = Dialog.TriggerProps;

const Trigger = Dialog.Trigger;

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_NAME = "DeleteDialogContent";

interface ContentContextValue {
  onDisabledChange: (disabled: boolean) => void;
}

const [ContentContextProvider, useContentContext] = createContext<ContentContextValue>(CONTENT_NAME);

interface ContentProps extends Dialog.ContentProps {
  onDelete: () => void;
  cancelLabel: string;
  deleteLabel: string;
}

const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ onDelete, cancelLabel, deleteLabel, className, children, ...props }, forwardedRef) => {
    const [disabled, setDisabled] = useState(false);

    return (
      <ContentContextProvider onDisabledChange={setDisabled}>
        <Dialog.Content {...props} ref={forwardedRef} className={cn("select-none", className)}>
          <Dialog.Description asChild>
            <div>{children}</div>
          </Dialog.Description>
          <div className="gap-10 flex items-center">
            <Dialog.Close asChild>
              <ActionButton variant="cancel">{cancelLabel}</ActionButton>
            </Dialog.Close>
            <ActionButton variant="danger" onClick={onDelete} disabled={disabled}>
              {deleteLabel}
            </ActionButton>
          </div>
        </Dialog.Content>
      </ContentContextProvider>
    );
  },
);

Content.displayName = CONTENT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Validator
 * -----------------------------------------------------------------------------------------------*/

const VALIDATOR_NAME = "DeleteDialogValidator";

type ValidatorProps = Omit<CaptchaFieldProps, "onValidChange">;

const Validator = forwardRef<HTMLDivElement, ValidatorProps>(({ className, ...props }, forwardedRef) => {
  const { onDisabledChange } = useContentContext(VALIDATOR_NAME);

  return (
    <CaptchaField
      {...props}
      ref={forwardedRef}
      onValidChange={valid => onDisabledChange(!valid)}
      className={cn("mx-auto", className)}
    />
  );
});

Validator.displayName = VALIDATOR_NAME;

export { Content, Root, Trigger, Validator };
export type { ContentProps, RootProps, TriggerProps, ValidatorProps };
